import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/base/Pagination';
import CategoryFilters, { type CategoryFilterState } from '@/pages/category/components/CategoryFilters';
import { movies } from '@/mocks/movies';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 8;

function buildInitialFilters(
  type: string | null,
  genre: string | null,
  region: string | null,
  year: string | null,
): CategoryFilterState {
  const types = type === 'movie' || type === 'tv' ? [type] : [];
  const parsedYear = year ? Number(year) : NaN;
  return {
    types,
    genres: genre ? [genre] : [],
    years: Number.isFinite(parsedYear) ? [parsedYear] : [],
    regions: region ? [region] : [],
    sort: 'date',
  };
}

export default function Category() {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<CategoryFilterState>(() =>
    buildInitialFilters(
      searchParams.get('type'),
      searchParams.get('genre'),
      searchParams.get('region'),
      searchParams.get('year'),
    ),
  );
  const [page, setPage] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const categoryName = useMemo(() => {
    if (filters.types.length === 1) return filters.types[0] === 'movie' ? '十八禁电影' : '十八禁电视剧';
    return '十八禁影视全集';
  }, [filters.types]);

  useSeo(
    `${categoryName} - 十八禁影视大全`,
    `${categoryName}分类页，支持按类型、上映年份和地区多维筛选，并可切换最新上映或热门排序，快速找到想看的成人向影视作品。`,
  );

  const filtered = useMemo(() => {
    const list = movies.filter((m) => {
      if (filters.types.length > 0 && !filters.types.includes(m.type)) return false;
      if (filters.genres.length > 0 && !m.genres.some((g) => filters.genres.includes(g))) return false;
      if (filters.years.length > 0 && !filters.years.includes(m.year)) return false;
      if (filters.regions.length > 0 && !filters.regions.includes(m.region)) return false;
      return true;
    });

    return [...list].sort((a, b) =>
      filters.sort === 'hot' ? b.views - a.views : b.year - a.year || b.views - a.views,
    );
  }, [filters]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggle = (group: 'types' | 'genres' | 'years' | 'regions', value: string | number) => {
    setFilters((prev) => {
      if (group === 'types') {
        if (value === 'all') return { ...prev, types: [] };
        const list = prev.types.includes(value as string)
          ? prev.types.filter((v) => v !== value)
          : [...prev.types, value as string];
        return { ...prev, types: list };
      }
      const current = prev[group] as (string | number)[];
      const list = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [group]: list };
    });
  };

  const reset = () =>
    setFilters({ types: [], genres: [], years: [], regions: [], sort: 'date' });

  return (
    <div className="w-full pt-16 md:pt-20">
      <div className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-10 md:pb-14 bg-background-100/50 border-b border-background-200/60">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4 flex-wrap">
          <Link to="/" className="hover:text-primary-400 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">影视分类</span>
        </nav>
        <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 font-bold">{categoryName}</h1>
        <p className="mt-3 text-sm text-foreground-600 max-w-2xl leading-relaxed">
          通过类型、上映年份与地区组合筛选，配合排序快速缩小范围。多选条件会叠加生效，帮助你更精准地定位想看的作品。
        </p>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="hidden md:block mb-8">
          <CategoryFilters
            filters={filters}
            onToggle={toggle}
            onSortChange={(sort) => setFilters((p) => ({ ...p, sort }))}
            onReset={reset}
          />
        </div>

        <div className="md:hidden mb-5">
          <button
            type="button"
            onClick={() => setShowMobileFilters((v) => !v)}
            className="w-full flex items-center justify-between bg-background-100 border border-background-200 rounded-md px-4 py-3 text-sm text-foreground-800 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line text-accent-400" />
              筛选与排序
            </span>
            <i className={showMobileFilters ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
          </button>
          {showMobileFilters && (
            <div className="mt-3 animate-fade-in">
              <CategoryFilters
                filters={filters}
                onToggle={toggle}
                onSortChange={(sort) => setFilters((p) => ({ ...p, sort }))}
                onReset={reset}
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-foreground-600">
            共找到 <span className="text-accent-400 font-bold font-label">{filtered.length}</span> 部作品
          </p>
          <p className="text-xs text-foreground-500 hidden sm:block">
            排序：{filters.sort === 'hot' ? '最热热门' : '最新上映'}
          </p>
        </div>

        {paged.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {paged.map((movie, i) => (
                <MovieCard key={movie.id} movie={movie} index={i} />
              ))}
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="py-20 text-center bg-background-100 border border-background-200/70 rounded-lg">
            <span className="mx-auto w-14 h-14 rounded-full bg-secondary-100 flex items-center justify-center mb-4">
              <i className="ri-search-eye-line text-2xl text-foreground-500" />
            </span>
            <p className="text-foreground-800 text-base">暂无符合条件的影视作品</p>
            <p className="mt-2 text-sm text-foreground-500">试着减少或调整筛选条件，或许会有更多结果。</p>
            <button
              type="button"
              onClick={reset}
              className="mt-6 inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm rounded-md px-5 py-2.5 whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        )}
      </div>
    </div>
  );
}